import { useEffect, useLayoutEffect, useRef, useState } from "react";
import SearchField from "../SearchField";
import "./index.css";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import SvgComponent from "../../SVGComponent";
import { standardAccountNumberFormat } from "../../../../utils/standardAccountNumberFormat";
import {
  dropdownHierarchy,
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import Chips from "../../CommonButtons/Chips";
import IconRoundButton from "../../CommonButtons/IconRoundButton";
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import SingleSelectDropDownComponent from "../../CommonDropdown/SingleSelectDropdown";
import MultiSelectDropDownComponent from "../../CommonDropdown/MultiSelectDropdown";
import CommonCalendar from "../../CommonCalendar";
import PrimaryRightIconFill from "../../CommonButtons/PrimaryRightIconFill";
import TickerAnimation from "../../TickerAnimation";
import Validation from "../../../../utils/validation";
import AmountRangeDropdown from "../../CommonDropdown/AmountRangeDropdown";
import ICONS from "../../../../assets/Icons";

/**
 * @typedef {Object} filterItems
 * @property {string} name - Name of the search category (e.g., "Amount", "Category")
 * @property {string} short_name - Short label used inside the Chips (e.g., "Amt", "Cat")
 * @property {boolean} isMulti - Determines if multiple values can be selected for this field
 * @property {any} value - The actual state value of the filter
 * @property {string?} displayValue - Formatted string for UI display (e.g., currency formatted amount)
 * @property {Function} setValue - Function to update the specific filter value
 * @property {Function?} setDisplayValue - Optional function to update a separate display state
 * @property {boolean} isDropdown - Whether this item triggers a result list from inputData
 * @property {("string" | "number")} valueType - The type of data expected
 * @property {boolean} isDisabled - If true, the specific filter line is non-interactive
 */

/**
 * @typedef {Object} filters
 * @property {string} name - The label for the filter field
 * @property {("dropdown" | "calender" | "amountRange")} filterType - Determines the UI component to render
 * @property {("single" | "range")} variant - Selection mode (single item or date range)
 * @property {string[]?} options - Selection options for dropdown types
 * @property {boolean?} isSearch - Enables internal search within the dropdown
 * @property {string?} dropdownPlaceHolder - Placeholder text for the dropdown
 * @property {string?} searchPlaceholder - Placeholder text for the dropdown's internal search
 * @property {any} value - The current state value of the filter
 * @property {Function} handleChange - Function called when the filter value changes
 * @property {boolean?} isDisabled - Disables the filter field
 * @property {number?} startDate - Start date  for calendar types
 * @property {number?} endDate - End date  for calendar types
 * @property {("small" | "large")} size - The size variant for the dropdown
 * @property {Object} amountLimitState - The current state for amount range eg:- amountLimitState({ min: "",max: "",})
 * @property {Object} setAmountLimitState - The setter state for amount range / Function called when the filter value changes
 * @property {Boolean} setShowAmtFilterDiv - Function called when the amount range dropdown open and close
 * @property {Boolean} setIsAmtFilterApplied - To indicate whether the amount range is applied or not
 */

/**
 * @typedef {Object} sortByFilter
 * @property {string} name - Display name of the sort option
 * @property {boolean?} default - If true, treats this as the default/reset option
 * @property {boolean?} isDisabled - Disables the sort button
 */

/**
 * @typedef FilteredSearchProps
 * @property {Function} props.onSearchInput - Logic to process text input from the search field
 * @property {Object} props.searchInputRef - React ref for the main search input container
 * @property {string} props.searchText - The current value of the search input string
 * @property {string} props.searchPlaceholder - Default placeholder for the search field
 * @property {boolean} props.isSearchDisabled - Global disabled state for the input
 * @property {boolean} props.isColumnDisabled - Global disabled state for the column
 * @property {boolean} props.isFilterDisabled - Global disabled state for the filter
 * @property {boolean} props.isSortDisabled - Global disabled state for the sort
 * @property {filterItems[]} props.filterItems - Array of searchable category definitions
 * @property {string} props.filterPlaceholder - Text shown in the "Search in..." prompt
 * @property {boolean} props.loading - Indicates if data is fetching (disables actions/shows loaders)
 * @property {Object[]} props.inputData - Data rows to be displayed in the dropdown result list
 * @property {Function} props.setShowInnerSearchBar - Controls visibility of the expanded search state
 * @property {boolean} props.showSearchBar - Master toggle for the component's expanded/collapsed animation
 * @property {boolean} props.isShowFilter - Toggles the visibility of the Filter icon
 * @property {boolean} props.isShowColumn - Toggles the visibility of the Column icon
 * @property {boolean} props.isShowSortBy - Toggles the visibility of the Sort icon
 * @property {filters[]} props.filters - Configuration for the advanced filter section
 * @property {boolean} props.isFilterClearAllDisabled - Disables the 'Clear all' button in the Filter view
 * @property {boolean} props.isSearchClearAllDisabled - Disables the 'Clear all' button in the active Chips view
 * @property {sortByFilter[]} props.sortByFilter - Configuration for the Sort By section
 * @property {string} props.sortValue - Current active sort selection
 * @property {Function} props.sortHandleChange - Handler for updating sort selection
 * @property {Function} props.columnHandleChange - Handler for updating column selection
 * @property {string} props.active - Current active filter category name
 * @property {Function} props.setActive - Setter for the active filter category
 * @property {Boolean} props.isError - error true if api fails
 * @property {Array} props.columnOptions - columns option
 * @property {Boolean} props.loadingData - for showing loader in the dropdown search

 * @param {FilteredSearchProps} props
 * @returns {JSX.Element}*/
const FilteredSearchField = ({
  openFilter,
  setOpenFilter,
  onSearchInput,
  searchInputRef,
  searchText,
  searchPlaceholder,
  isSearchDisabled,
  filterItems,
  filterPlaceholder,
  loading,
  inputData,
  setShowInnerSearchBar,
  setActive,
  showSearchBar,
  isShowFilter,
  isShowSortBy,
  filters,
  isFilterClearAllDisabled,
  isFilterDisabled,
  isSortDisabled,
  isSearchClearAllDisabled,
  sortByFilter,
  sortValue,
  sortHandleChange,
  isTopNav,
  isTopNavSearch,
  topNavRef,
  setTopNavSearch,
  setIsFilterOpen,
  setIsSortOpen,
  isError,
  loadingData,
  isShowColumn,
  setIsColumnOpen,
  columnValue,
  columnHandleChange,
  isColumnDisabled,
  columnOptions,
  isColumnDefaultDisabled,
  isSortDefaultDisabled,
  handleColumnDefault,
  statementType,
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownanimate, setdropdownanimate] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showSortBy, setShowSortBy] = useState(false);
  const [showColumn, setShowColumn] = useState(false);
  const [currentDropdown, setCurrentDropdown] = useState("");
  const searchRef = useRef();
  const searchFRef = useRef();

  const isSearchApplied = filterItems?.some(
    (filter) => String(filter?.value)?.length,
  );
  const SearchAppliedCount = filterItems?.filter(
    (filter) => String(filter?.value)?.length,
  );
  const isFilterApplied = filters?.some(
    (itm) => itm?.value?.length || itm?.value,
  );

  useEffect(() => {
    const handleFilter = async () => {
      if (openFilter) {
        setShowFilter(true);
        setShowInnerSearchBar(true);
        setTopNavSearch?.(true);
        setIsColumnOpen?.(true);
      } else {
        await setShowFilter(false);
        setShowInnerSearchBar(false);
        setTopNavSearch?.(false);
        setIsColumnOpen?.(false);
      }
    };
    handleFilter();
    const handleDropDown = async (e) => {
      if (searchFRef.current && searchFRef.current.contains(e.target)) return;
      await setShowFilter(false);
      await setIsFilterOpen?.(false);
      setShowDropdown(false);
      setShowSearch(false);

      if (!isTopNav) {
        setShowInnerSearchBar(false);
      }

      setShowSortBy(false);
      setShowColumn(false);
      setIsColumnOpen?.(false);
      setIsSortOpen?.(false);
    };

    document.addEventListener("mousedown", handleDropDown);
    return () => {
      document.removeEventListener("mousedown", handleDropDown);
    };
  }, [isTopNav, openFilter]);

  const ChipButton = ({ data, short }) => {
    if (data?.isMulti) {
      return data?.value?.map((chipData, index) => {
        return (
          <Chips
            isTickerRequired={true}
            key={index}
            txtChip={
              short
                ? data?.displayValue
                  ? data?.displayValue
                  : data.isMulti
                    ? String(data?.value?.[index])
                    : data?.value
                : `${data.short_name} ${data?.name?.toLowerCase()?.includes("amount") || data?.valueType === "number" ? "" : "-"} ${data?.displayValue ? data?.displayValue : data.isMulti ? String(data?.value?.[index]) : data?.value}`
            }
            icon={ICONS?.CLOSE_WITH_BLUE_CIRCLE}
            enabledBgColor="var(--primary-nav)"
            outLineColor=""
            isDisabled={
              data?.isDisabled ||
              (data?.isAlphabetical &&
                !Validation.alphanumericRegex.test(searchText) &&
                !Validation.alphaRegex.test(searchText) &&
                short) ||
              ((data?.name?.toLowerCase()?.includes("amount") ||
                data?.valueType === "number") &&
                isNaN(searchText) &&
                short) ||
              ((data.name.toLowerCase()?.includes("amount") ||
                data?.valueType === "number") &&
                searchText?.length < GlobalConfiguration.lengthCheckOne &&
                short) ||
              (!data.name.toLowerCase()?.includes("amount") &&
                !data?.valueType === "number" &&
                searchText?.length < GlobalConfiguration.searchMinLength &&
                short)
            }
            disableIcon={
              data.isDisabled ||
              (data?.isAlphabetical &&
                Validation.alphanumericRegex.test(
                  searchText?.replaceAll(" ", ""),
                ) &&
                !Validation.alphaRegex.test(searchText?.replaceAll(" ", "")) &&
                short) ||
              ((data?.name?.toLowerCase()?.includes("amount") ||
                data?.valueType === "number") &&
                (isNaN(searchText) ||
                  searchText?.length ===
                    GlobalConfiguration?.lengthCheckZero) &&
                short) ||
              (!(
                data?.name?.toLowerCase()?.includes("amount") ||
                data?.valueType === "number"
              ) &&
                searchText?.length < GlobalConfiguration.searchMinLength &&
                short)
            }
            handleFunction={(e) => {
              e.stopPropagation();
              if (
                data?.isDisabled ||
                ((data?.name?.toLowerCase()?.includes("amount") ||
                  data?.valueType === "number") &&
                  isNaN(searchText) &&
                  short) ||
                (data?.isAlphabetical &&
                  !Validation.alphanumericRegex.test(searchText) &&
                  !Validation.alphaRegex.test(searchText) &&
                  short) ||
                ((data.name.toLowerCase()?.includes("amount") ||
                  data?.valueType === "number") &&
                  searchText?.length < GlobalConfiguration.lengthCheckOne &&
                  short) ||
                (!data.name.toLowerCase()?.includes("amount") &&
                  !data?.valueType === "number" &&
                  searchText?.length < GlobalConfiguration.searchMinLength &&
                  short)
              ) {
              } else {
                if (data?.isMulti) {
                  if (data?.value?.includes(chipData)) {
                    let newData = data?.value?.filter((val) => {
                      return val != chipData;
                    });
                    e.stopPropagation();
                    data?.setValue(newData);
                  }
                } else {
                  data?.setValue("");
                }
              }
            }}
            enabledTextColor="var(--text-primary)"
          />
        );
      });
    } else {
      return (
        <Chips
          isTickerRequired={true}
          txtChip={
            data?.name?.toLowerCase() === "search"
              ? data?.value
              : short
                ? data?.displayValue
                  ? data?.displayValue
                  : String(data?.value)
                : `${data.short_name || ""} ${data.name.toLowerCase()?.includes("amount") || data?.valueType === "number" ? "" : data.short_name ? "-" : ""} ${data?.displayValue ? data?.displayValue : String(data?.value)}`
          }
          icon={ICONS?.CLOSE_WITH_BLUE_CIRCLE}
          enabledBgColor="var(--primary-nav)"
          outLineColor=""
          disableIcon={
            data.isDisabled ||
            (data?.isAlphabetical &&
              Validation.alphanumericRegex.test(
                searchText?.replaceAll(" ", ""),
              ) &&
              !Validation.alphaRegex.test(searchText?.replaceAll(" ", "")) &&
              short) ||
            ((data?.name?.toLowerCase()?.includes("amount") ||
              data?.valueType === "number") &&
              (isNaN(searchText) ||
                searchText?.length === GlobalConfiguration?.lengthCheckZero) &&
              short) ||
            (!(
              data?.name?.toLowerCase()?.includes("amount") ||
              data?.valueType === "number"
            ) &&
              searchText?.length < GlobalConfiguration.searchMinLength &&
              short &&
              short)
          }
          isDisabled={
            data?.isDisabled ||
            ((data?.name?.toLowerCase()?.includes("amount") ||
              data?.valueType === "number") &&
              isNaN(searchText) &&
              short) ||
            (data?.isAlphabetical &&
              !Validation.alphanumericRegex.test(searchText) &&
              !Validation.alphaRegex.test(searchText) &&
              short) ||
            ((data.name.toLowerCase()?.includes("amount") ||
              data?.valueType === "number") &&
              searchText?.length < GlobalConfiguration.lengthCheckOne &&
              short) ||
            (!data.name.toLowerCase()?.includes("amount") &&
              !data?.valueType === "number" &&
              searchText?.length < GlobalConfiguration.searchMinLength &&
              short)
          }
          handleFunction={(e) => {
            e.stopPropagation();
            if (
              data?.isDisabled ||
              ((data?.name?.toLowerCase()?.includes("amount") ||
                data?.valueType === "number") &&
                isNaN(searchText) &&
                short) ||
              (data?.isAlphabetical &&
                !Validation.alphanumericRegex.test(searchText) &&
                !Validation.alphaRegex.test(searchText) &&
                short) ||
              ((data.name.toLowerCase()?.includes("amount") ||
                data?.valueType === "number") &&
                searchText?.length < GlobalConfiguration.lengthCheckOne &&
                short) ||
              (!data.name.toLowerCase()?.includes("amount") &&
                !data?.valueType === "number" &&
                searchText?.length < GlobalConfiguration.searchMinLength &&
                short)
            ) {
            } else {
              data?.setValue("");
            }
          }}
          enabledTextColor="var(--text-primary)"
        />
      );
    }
  };

  const renderFilter = (filter) => {
    switch (filter?.filterType) {
      case "calender":
        return (
          <div
            style={{ minWidth: "var(--dropdown-width)" }}
            className="flex flex-col w-full Qsmd:w-dropdown-width gap-y-gap-xs"
          >
            <p className="text-text-secondary text-font-xs font-normal">
              {filter?.name}
            </p>
            <div
            // className={`${filter?.isDisabled ? "opacity-light-opacity pointer-events-none" : ""}`}
            >
              <CommonCalendar
                isOpen={openFilter}
                startDate={filter?.startDate}
                isDisabled={filter?.isDisabled}
                endDate={filter?.endDate}
                handleDateChange={(val) => {
                  filter.handleChange(val);
                  setShowInnerSearchBar(false);
                  setShowFilter(false);
                  if (setIsFilterOpen) {
                    setIsFilterOpen(false);
                  }

                  setTopNavSearch(false);

                  setShowSearch(false);
                }}
                type={"small"}
                selectionType={filter?.variant}
                minDate={filter?.minDate}
                maxDate={filter?.maxDate}
              />
            </div>
          </div>
        );
      case "accountSelect":
        return (
          <div
            style={{ minWidth: "var(--dropdown-width)" }}
            className="flex flex-col w-full Qsmd:w-dropdown-width gap-y-gap-xs"
          >
            <p className="text-text-secondary text-font-xs font-normal">
              {filter?.name}
            </p>
            <div
              className={`${filter?.isDisabled ? "opacity-light-opacity pointer-events-none" : ""}`}
            >
              <PrimaryRightIconFill
                {...filter?.btnProps}
                btnType="button"
                imgColor="var(--text-primary)"
                icon={ICONS?.ARROW_RIGHT_WHITE}
              />
            </div>
          </div>
        );

      case "dropdown":
        return (
          <div
            style={{ minWidth: "var(--dropdown-width)" }}
            className="flex flex-col w-full Qsmd:w-dropdown-width gap-y-gap-xs"
          >
            <p className="text-text-secondary text-font-xs font-normal">
              {filter?.name}
            </p>
            {filter?.variant?.toLowerCase() === "single" ? (
              <SingleSelectDropDownComponent
                type="small"
                options={filter.options}
                selected={filter.value}
                setSelected={(val) => {
                  filter.handleChange(val);
                  setShowInnerSearchBar(false);
                  setShowFilter(false);
                  if (setIsFilterOpen) {
                    setIsFilterOpen(false);
                  }

                  setTopNavSearch(false);

                  setShowSearch(false);
                }}
                showSearch={filter.isSearch}
                isOpen={false}
                placeholder={filter.dropdownPlaceHolder}
                searchPlaceholder={filter.searchPlaceholder}
                disabled={filter?.isDisabled}
                statusPop={filter?.statusPop}
                dropdownType={filter?.dropdownType}
              />
            ) : (
              <MultiSelectDropDownComponent
                type="small"
                options={filter.options}
                selected={filter.value}
                setSelected={(val) => {
                  filter.handleChange(val);
                  setShowInnerSearchBar(false);
                  setShowFilter(false);
                  if (setIsFilterOpen) {
                    setIsFilterOpen(false);
                  }

                  setTopNavSearch(false);

                  setShowSearch(false);
                }}
                showSearch={filter.isSearch}
                isOpen={false}
                placeholder={filter.dropdownPlaceHolder}
                searchPlaceholder={filter.searchPlaceholder}
              />
            )}
          </div>
        );
      case "amountRange":
        return (
          <div
            style={{ minWidth: "var(--dropdown-width)" }}
            className="flex flex-col w-full Qsmd:w-dropdown-width gap-y-gap-xs"
          >
            <p className="text-text-secondary text-font-xs font-normal">
              Amount range
            </p>
            <AmountRangeDropdown
              placeholder="Set amount range"
              type="small"
              range={filter?.rangeValue}
              setRange={filter?.handleChange}
              onOpen={() => filter?.setShowAmtFilterDiv(true)}
              onClose={() => filter?.setShowAmtFilterDiv(false)}
              onApply={(range) => {
                filter?.setIsAmtFilterApplied(true);
                setShowInnerSearchBar(false);
                setShowFilter(false);
                if (setIsFilterOpen) {
                  setIsFilterOpen(false);
                }

                setTopNavSearch(false);

                setShowSearch(false);
              }}
              onClear={() => {
                filter?.handleChange((prev) => ({
                  ...prev,
                  min: "",
                  max: "",
                }));
                filter?.setIsAmtFilterApplied(false);
                setShowInnerSearchBar(false);
                setShowFilter(false);
                if (setIsFilterOpen) {
                  setIsFilterOpen(false);
                }

                setTopNavSearch(false);

                setShowSearch(false);
              }}
              disabled={filter?.isDisabled}
            />
          </div>
        );

      default:
        break;
    }
  };

  const LineMapper = ({ data, index }) => {
    return (
      <>
        <div className="flex flex-1 justify-start gap-x-gap-sm items-center overflow-x-hidden">
          <p className="text-text-primary font-normal  text-font-xs py-padding-button-m px-padding-sm bg-primary-nav rounded-border-radius-md min-w-max">
            {data?.name}
          </p>
          <SvgComponent
            url={ICONS?.SEARCH_WHITE}
            color="var(--text-primary)"
            height="var(--icon-sm)"
            width="var(--icon-sm)"
            alt="search"
          />
          {String(data?.value).length ? (
            <div
              className={`flex overflow-x-auto gap-x-gap-sm  ${data.isMulti ? "pb-padding-xs" : ""}`}
            >
              <ChipButton short={true} data={data} />
            </div>
          ) : null}
          <div className="flex gap-gap-sm flex-1  min-w-min-width-base overflow-hidden">
            {data?.value?.length ? (
              ""
            ) : (!(
                data?.name?.toLowerCase()?.includes("amount") ||
                data?.valueType === "number"
              ) &&
                searchText?.length < GlobalConfiguration.searchMinLength &&
                !data?.isAlphabetical) ||
              (data?.isAlphabetical &&
                Validation.alphanumericRegex.test(
                  searchText?.replaceAll(" ", ""),
                ) &&
                Validation.alphaRegex.test(searchText?.replaceAll(" ", "")) &&
                searchText?.length < GlobalConfiguration.searchMinLength) ? (
              <p className="text-text-secondary text-font-xs font-normal break-all">
                {"in "}{" "}
                {data?.name?.toLowerCase() === "search"
                  ? filterPlaceholder
                  : data.name}
                {". "}
                Minimum 3 characters required.
              </p>
            ) : data?.isAlphabetical &&
              Validation.alphanumericRegex.test(
                searchText?.replaceAll(" ", ""),
              ) &&
              !Validation.alphaRegex.test(searchText?.replaceAll(" ", "")) ? (
              <p className="text-text-secondary text-font-xs font-normal break-all">
                {"in "}{" "}
                {data?.name?.toLowerCase() === "search"
                  ? filterPlaceholder
                  : data.name}
                {". "}
                Only alphabetical values allowed.
              </p>
            ) : (data?.name?.toLowerCase()?.includes("amount") ||
                data?.valueType === "number") &&
              (isNaN(searchText) ||
                searchText?.length === GlobalConfiguration?.lengthCheckZero) ? (
              <p className="text-text-secondary text-font-xs font-normal break-all">
                {"in "}{" "}
                {data?.name?.toLowerCase() === "search"
                  ? filterPlaceholder
                  : data.name}
                {". "}
                Only numericals values allowed.
              </p>
            ) : (
              <p className="text-text-secondary text-font-xs font-normal break-all">
                {data?.name?.toLowerCase() === "search"
                  ? `"${searchText}"`
                  : searchText}{" "}
                in{" "}
                {data?.name?.toLowerCase() === "search"
                  ? filterPlaceholder
                  : data.name}
              </p>
            )}
          </div>
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (
              searchText?.length >= GlobalConfiguration.searchMinLength &&
              !data.isDisabled
            )
              data.setValue([]);
          }}
          className={`${
            data.isMulti &&
            data.value?.length > GlobalConfiguration.lengthCheckZero
              ? "flex min-w-min "
              : "hidden"
          }`}
        >
          <img className="w-icon-sm h-icon-sm" src={ICONS?.CLOSE} alt="Close" />
        </div>
        <div
          className={`${
            data.isMulti &&
            data.value?.length > GlobalConfiguration.lengthCheckZero
              ? " border-r-primary-nav border-r-border h-[var(--filtered-search-height)]"
              : "hidden"
          }`}
        ></div>
        <div
          style={{
            display:
              data.isDropdown && !data.isDisabled ? "flex min-w-min" : "none",
          }}
        >
          <SvgComponent
            url={
              showDropdown && currentDropdown === index
                ? ICONS?.ARROW_UP_WHITE
                : ICONS?.ARROW_DOWN_WHITE
            }
            color="var(--text-primary)"
            height="var(--icon-sm)"
            width="var(--icon-sm)"
            alt="down"
          />
        </div>
      </>
    );
  };
  useEffect(() => {
    if (!isTopNav) return;

    if (isTopNavSearch) {
      setShowInnerSearchBar(true);
    } else {
      onSearchInput("");
      setShowSearch(false);
      setShowColumn(false);
      setShowInnerSearchBar(false);
    }
  }, [isTopNavSearch, isTopNav]);

  return (
    <div
      ref={searchFRef}
      style={{
        overflow: "visible",
      }}
      className={`${isTopNav ? "transition-all duration-animation-medium ease-in-out scale-y-100 w-width-full" : "searchFilteredComponent py-padding-sm"} origin-top ${isTopNav ? "relative" : "box-content  "}  py-padding-sm ${
        isTopNav
          ? "max-h-0 "
          : showSearchBar
            ? "searchFilteredComponent-expand"
            : "searchFilteredComponent-collapse"
      }`}
    >
      <div
        ref={searchInputRef}
        style={{
          maxWidth: "var(--max-content-width)",
          top:
            isTopNav && (isShowFilter || isShowSortBy || isShowColumn)
              ? "calc(var(--negative-global-var) * var(--top-nav-min-height))"
              : !isShowFilter && !isShowSortBy && !isShowColumn
                ? "calc(var(--negative-global-var) * var(--mobile-padding-xl))"
                : "var(--top-base)",
        }}
        className={`relative origin-top w-full flex flex-col justify-between h-auto bg-primary-bg rounded-border-radius-md `}
      >
        <div
          onClick={(e) => {
            if (isSearchDisabled) return;
            setShowSearch(true);
            setShowInnerSearchBar(true);
          }}
          style={{ minHeight: "var(--filtered-search-height)" }}
          className={`w-full flex flex-col justify-between items-start `}
        >
          <div
            className={`${
              ((isSearchApplied || showFilter || showSortBy || showColumn) &&
                !isTopNav) ||
              (isTopNavSearch &&
                (isSearchApplied || showFilter || showSortBy || showColumn))
                ? "filter-search-borderdiv-lg"
                : ""
            } relative w-full flex items-center `}
          >
            <SearchField
              loading={loading}
              onInput={(val) => {
                if (val?.length <= GlobalConfiguration.searchMaxLength) {
                  setShowFilter(false);
                  if (setIsFilterOpen) {
                    setIsFilterOpen(false);
                  }
                  setShowSortBy(false);
                  setShowColumn(false);
                  if (setIsSortOpen) {
                    setIsSortOpen(false);
                  }
                  if (setIsColumnOpen) {
                    setIsColumnOpen(false);
                  }
                  if (isTopNav) {
                    setTopNavSearch(true);
                  }
                  setShowInnerSearchBar(true);
                  setShowSearch(true);

                  setShowDropdown(false);
                  setActive("");
                  onSearchInput(val?.replace(/\s+/g, " ").trimStart());
                }
              }}
              searchText={searchText}
              placeholder={
                isSearchApplied
                  ? `${searchPlaceholder} (${SearchAppliedCount?.length} ${SearchAppliedCount?.length > GlobalConfiguration.lengthCheckOne ? "fields are active" : "field is active"}) `
                  : searchPlaceholder
              }
              isDisabled={isSearchDisabled}
              isTopNav={isTopNav}
              isTopNavSearch={isTopNavSearch}
              searchRef={topNavRef}
            />
            {isShowFilter || isShowSortBy || isShowColumn ? (
              <div className="flex gap-x-gap-sm py-padding-sm pr-padding-md filter-border-left relative">
                {isShowFilter ? (
                  <IconRoundButton
                    enabledBgColor={
                      isFilterApplied
                        ? "var(--text-primary)"
                        : "var(--primary-nav-act)"
                    }
                    handleFunction={(e) => {
                      if (loading || isFilterDisabled) {
                        e.stopPropagation();

                        return;
                      } else {
                        e.stopPropagation();
                        setShowInnerSearchBar(true);
                        if (isTopNav) {
                          setTopNavSearch(true);
                        }
                        setShowFilter(!showFilter);

                        setShowSearch(false);
                        setShowSortBy(false);
                        setShowColumn(false);
                        if (setIsSortOpen) {
                          setIsSortOpen(false);
                        }
                        if (setIsColumnOpen) {
                          setIsColumnOpen(false);
                        }
                        if (setIsFilterOpen) {
                          setIsFilterOpen(!showFilter);
                        }
                        onSearchInput("");
                      }
                    }}
                    hoverOption={isFilterApplied ? "black" : "white"}
                    icon={ICONS?.FILTER}
                    title="Filter"
                    isDisabled={isFilterDisabled || loading}
                    imgColor={
                      isFilterApplied
                        ? "var(--primary-bg)"
                        : "var(--text-primary)"
                    }
                  />
                ) : null}
                {isShowSortBy ? (
                  <IconRoundButton
                    enabledBgColor={
                      sortValue?.length
                        ? "var(--text-primary)"
                        : "var(--primary-nav-act)"
                    }
                    handleFunction={(e) => {
                      if (loading || isSortDisabled) {
                        e.stopPropagation();

                        return;
                      } else {
                        e.stopPropagation();
                        setShowInnerSearchBar(true);
                        setShowSearch(false);
                        setShowFilter(false);
                        if (setIsFilterOpen) {
                          setIsFilterOpen(false);
                        }
                        setShowSortBy(!showSortBy);
                        setShowColumn(false);
                        if (setIsSortOpen) {
                          setIsSortOpen(!showSortBy);
                        }
                        if (setIsColumnOpen) {
                          setIsColumnOpen(!showColumn);
                        }
                        onSearchInput("");
                        if (isTopNav) {
                          setTopNavSearch(true);
                        }
                      }
                    }}
                    hoverOption={sortValue?.length ? "black" : "white"}
                    icon={ICONS?.SORT_BY}
                    title="Sort by"
                    isDisabled={isSortDisabled || loading}
                    imgColor={
                      sortValue?.length
                        ? "var(--primary-bg)"
                        : "var(--text-primary)"
                    }
                  />
                ) : null}
                {isShowColumn ? (
                  <IconRoundButton
                    enabledBgColor={
                      isColumnDefaultDisabled
                        ? "var(--primary-nav-act)"
                        : "var(--text-primary)"
                    }
                    handleFunction={(e) => {
                      if (loading || isColumnDisabled) {
                        e.stopPropagation();

                        return;
                      } else {
                        e.stopPropagation();
                        setShowInnerSearchBar(true);
                        setShowSearch(false);
                        setShowFilter(false);
                        setShowSortBy(false);
                        setShowColumn(!showColumn);
                        if (setIsFilterOpen) {
                          setIsFilterOpen(false);
                        }
                        if (setIsSortOpen) {
                          setIsSortOpen(false);
                        }
                        if (setIsColumnOpen) {
                          setIsColumnOpen(!showColumn);
                        }
                        onSearchInput("");
                        if (isTopNav) {
                          setTopNavSearch(true);
                        }
                      }
                    }}
                    hoverOption={columnValue?.length ? "black" : "white"}
                    icon={ICONS?.ADDITIONAL_FIELDS_WHITE}
                    title="Columns"
                    isDisabled={isColumnDisabled || loading}
                    imgColor={
                      !isColumnDefaultDisabled
                        ? "var(--primary-bg)"
                        : "var(--text-primary)"
                    }
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {(isSearchApplied &&
            !showFilter &&
            !showSortBy &&
            !showColumn &&
            !isTopNav) ||
          (isTopNavSearch &&
            isSearchApplied &&
            !showFilter &&
            !showSortBy &&
            !showColumn) ? (
            <div className="w-full   px-padding-md flex pt-padding-sm pb-padding-sm gap-gap-sm   justify-between  relative">
              <div className="flex gap-x-gap-sm overflow-x-hidden flex-1">
                <p className="text-text-secondary text-font-xs font-normal my-auto">
                  Active:{" "}
                </p>
                <div className="flex overflow-x-auto gap-gap-sm my-auto pb-padding-xs">
                  {filterItems?.map((filter, index) => {
                    if (String(filter.value)?.length) {
                      return <ChipButton data={filter} key={index} />;
                    }
                  })}
                </div>
              </div>

              <div className="flex items-start min-w-min">
                <PrimaryButton
                  btnText="Clear all"
                  btnType="button"
                  enabledBgColor="transparent"
                  outLineColor={"var(--text-primary)"}
                  enabledTextColor="var(--text-primary)"
                  hoverOption="white"
                  isDisabled={isSearchClearAllDisabled || loading}
                  handleFunction={(e) => {
                    e.stopPropagation();
                    if (!isSearchClearAllDisabled && !loading) {
                      onSearchInput("");
                      setShowDropdown(false);
                      filterItems?.map((itm) => {
                        if (itm?.setValue && !itm?.isDisabled)
                          itm?.setValue("");
                      });
                      setShowSearch(false);
                      if (setTopNavSearch) {
                        setTopNavSearch(false);
                      }
                      setShowInnerSearchBar(false);
                    }
                  }}
                />
              </div>
            </div>
          ) : null}

          <>
            <div
              className={` ${showFilter || showSortBy || showColumn || showSearch ? ` visible ${showFilter || showSortBy || showColumn ? "overflow-x-hidden Qmd:overflow-visible overflow-y-auto max-h-mobile-filtered-search-maxheight  Qsmd:max-h-filtered-search-maxheight" : " !overflow-auto max-h-full"}  mb-margin-sm` : "invisible max-h-0 filteredsearchdivclose overflow-hidden  "} ${showColumn ? "!overflow-y-auto" : ""}  w-full`}
            >
              <div ref={searchRef} className="w-full flex flex-col ">
                {showFilter ? (
                  <div className="py-padding-sm flex-col flex Qsmd:flex-row w-full justify-between px-padding-md gap-y-top_md gap-x-gap-sm">
                    <div className="flex flex-1 flex-wrap gap-x-gap-md gap-y-gap-md">
                      {filters
                        ?.sort(
                          (a, b) =>
                            dropdownHierarchy[a.name?.toLowerCase()] -
                            dropdownHierarchy[b.name?.toLowerCase()],
                        )
                        ?.map((filter) => {
                          return renderFilter(filter);
                        })}
                    </div>
                    <div className="flex justify-end items-start min-w-min">
                      <PrimaryButton
                        fullWidthRequired
                        btnText="Clear all"
                        btnType="button"
                        enabledBgColor="transparent"
                        outLineColor={"var(--text-primary)"}
                        enabledTextColor="var(--text-primary)"
                        hoverOption="white"
                        isDisabled={isFilterClearAllDisabled || loading}
                        handleFunction={(e) => {
                          e.stopPropagation();
                          if (!isFilterClearAllDisabled && !loading) {
                            filters?.map((itm) => {
                              if (itm?.handleChange && !itm.isDisabled) {
                                itm?.handleChange("");
                                if (itm?.setIsAmtFilterApplied) {
                                  itm?.setIsAmtFilterApplied(false);
                                }
                              }
                            });

                            setShowInnerSearchBar(false);
                            setShowFilter(false);
                            if (setIsFilterOpen) {
                              setIsFilterOpen(false);
                            }
                            if (setTopNavSearch) {
                              setTopNavSearch(false);
                            }
                            setShowSearch(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : null}
                {showSortBy ? (
                  <div className="py-padding-sm flex-col flex Qsmd:flex-row w-full justify-between px-padding-md gap-y-top_md gap-x-gap-sm">
                    <div className="flex flex-1 flex-col gap-y-gap-md">
                      <div className="flex flex-1 flex-wrap gap-x-gap-md gap-y-gap-md">
                        {sortByFilter?.map((sort) => {
                          return (
                            <div
                              style={{ maxWidth: "var(--dropdown-width)" }}
                              className="w-dropdown-width"
                            >
                              <PrimaryRightIconFill
                                btnText={sort?.name}
                                btnType="button"
                                enabledBgColor={
                                  sort?.name === sortValue ||
                                  (sortValue === "" && sort?.default)
                                    ? "var(--positive-bg)"
                                    : "var(--primary-nav)"
                                }
                                enabledTextColor="var(--text-primary)"
                                hoverOption={
                                  sort?.name === sortValue ||
                                  (sortValue === "" && sort?.default)
                                    ? "black"
                                    : "white"
                                }
                                handleFunction={() => {
                                  if (!sort?.isDisabled)
                                    if (sort?.default) {
                                      if (sortValue !== "") {
                                        setShowInnerSearchBar(false);
                                        setShowSortBy(false);
                                        if (setIsSortOpen) {
                                          setIsSortOpen(false);
                                        }
                                        setTopNavSearch(false);

                                        setShowSearch(false);
                                      }
                                      sortHandleChange("");
                                    } else {
                                      sortHandleChange(sort?.name);
                                      setShowInnerSearchBar(false);
                                      setShowSortBy(false);
                                      if (setIsSortOpen) {
                                        setIsSortOpen(false);
                                      }
                                      setTopNavSearch(false);

                                      setShowSearch(false);
                                    }
                                }}
                                isDisabled={sort?.isDisabled}
                                icon={
                                  sort?.name === sortValue ||
                                  (sortValue === "" && sort?.default)
                                    ? ICONS?.RADIO_BUTTON_SELECTED
                                    : ICONS?.RADIO_BUTTON_NOT_SELECTED
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex justify-end items-start min-w-min">
                      <PrimaryButton
                        fullWidthRequired
                        btnText="Default"
                        btnType="button"
                        enabledBgColor="transparent"
                        outLineColor={"var(--text-primary)"}
                        enabledTextColor="var(--text-primary)"
                        hoverOption="white"
                        isDisabled={isSortDefaultDisabled || loading}
                        handleFunction={(e) => {
                          e.stopPropagation();
                          if (!isSortDefaultDisabled && !loading) {
                            sortHandleChange("");

                            setShowInnerSearchBar(false);
                            setShowSortBy(false);
                            if (setIsSortOpen) {
                              setIsSortOpen(false);
                            }
                            if (setTopNavSearch) {
                              setTopNavSearch(false);
                            }
                            setShowSearch(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : null}
                {showColumn ? (
                  <div className="py-padding-sm flex-col flex Qsmd:flex-row w-full justify-between px-padding-md gap-y-top_md gap-x-gap-sm">
                    <div className="flex flex-1 flex-col gap-y-gap-md">
                      <p className="text-text-primary text-font-sm font-normal">
                        Columns
                      </p>
                      <div className="flex flex-1 flex-wrap gap-x-gap-md gap-y-gap-md">
                        {columnOptions?.map((column) => {
                          return (
                            <div
                              style={{ maxWidth: "var(--dropdown-width)" }}
                              className="w-dropdown-width"
                            >
                              <PrimaryRightIconFill
                                btnText={
                                  column?.colName === "Amount"
                                    ? statementType === "Credit"
                                      ? "Credit amount"
                                      : statementType === "Debit"
                                        ? "Debit amount"
                                        : "Amount"
                                    : column?.colName
                                }
                                btnType="button"
                                enabledBgColor={
                                  columnValue.some(
                                    (x) => x.colName === column?.colName,
                                  )
                                    ? "var(--positive-bg)"
                                    : "var(--primary-nav)"
                                }
                                enabledTextColor="var(--text-primary)"
                                hoverOption={
                                  columnValue.some(
                                    (x) => x.colName === column?.colName,
                                  )
                                    ? "black"
                                    : "white"
                                }
                                handleFunction={() => {
                                  if (!column?.isDisabled) {
                                    columnHandleChange(column?.colName);
                                  }
                                }}
                                isDisabled={column?.isDisabled}
                                icon={
                                  columnValue.some(
                                    (x) => x.colName === column?.colName,
                                  )
                                    ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                                    : ICONS?.SELECT_ALL_WITH_WHITE_OUTLINE_CIRCLE
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex justify-end items-start min-w-min">
                      <PrimaryButton
                        fullWidthRequired
                        btnText="Default"
                        btnType="button"
                        enabledBgColor="transparent"
                        outLineColor={"var(--text-primary)"}
                        enabledTextColor={"var(--text-primary)"}
                        hoverOption="white"
                        isDisabled={isColumnDefaultDisabled || loading}
                        handleFunction={(e) => {
                          e.stopPropagation();
                          if (!isColumnDefaultDisabled && !loading) {
                            handleColumnDefault();

                            setShowInnerSearchBar(false);
                            setShowColumn(false);
                            if (setIsColumnOpen) {
                              setIsColumnOpen(false);
                            }
                            if (setTopNavSearch) {
                              setTopNavSearch(false);
                            }
                            setShowSearch(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
              {filterItems?.length ? (
                <div
                  className={`${
                    showFilter || showSortBy || showColumn
                      ? "invisible max-h-0 filteredsearchdivclose "
                      : showSearch && !showFilter && !showSortBy && !showColumn
                        ? "visible filteredsearchdiv pt-padding-sm overflow-hidden"
                        : "invisible max-h-0 filteredsearchdivclose "
                  } w-full px-padding-sm gap-y-gap-sm flex flex-col`}
                >
                  <div className="w-full flex flex-col gap-y-gap-md justify-between items-start">
                    {filterItems?.map((listData, index) => {
                      return (
                        <>
                          {listData.isDropdown &&
                          showDropdown &&
                          currentDropdown === index ? (
                            <div
                              key={index}
                              className={`${dropdownanimate ? "filtered-search-dropdown " : "filtered-search-dropdown-close "} ${inputData?.length > GlobalConfiguration.lengthCheckFive && !loading && !isError ? "pb-padding-button-l" : "pb-top_sm"} pt-padding-xs filter-search-borderdiv after:m-margin-sm relative w-full bg-primary-nav-act rounded-border-radius-md py-padding-xs gap-y-gap-sm flex flex-col cursor-pointer`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setdropdownanimate(false);
                                setTimeout(() => {
                                  setdropdownanimate(true);
                                  setShowDropdown(false);
                                }, IntervalConfiguration.intervalSmaller);
                              }}
                            >
                              <div className="w-full flex justify-between items-center px-padding-sm gap-x-gap-sm filter-search-dropdown-borderdiv relative">
                                <LineMapper data={listData} index={index} />
                              </div>
                              <div className="flex flex-col h-full overflow-x-hidden break-all gap-y-gap-sm cursor-pointer overflow-y-auto w-full justify-between items-center py-padding-xs  relative ">
                                {loadingData ? (
                                  <div className="w-full flex justify-center items-center relative">
                                    <img
                                      className="w-loader-sm h-loader_w-loader-sm"
                                      src={ICONS?.LOADER_GIF}
                                      alt="loading"
                                    />
                                  </div>
                                ) : isError ? (
                                  <div className="w-full flex justify-center items-center relative">
                                    <p className="text-text-secondary text-font-xs font-normal">
                                      Failed to fetch details. Try again later.
                                    </p>
                                  </div>
                                ) : inputData?.length ? (
                                  <>
                                    {inputData?.map((itm, idx) => {
                                      return (
                                        <div
                                          key={idx}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (listData?.isMulti) {
                                              if (
                                                listData?.value?.includes(
                                                  itm.id,
                                                )
                                              ) {
                                                let data =
                                                  listData?.value?.filter(
                                                    (val) => {
                                                      return val != itm.id;
                                                    },
                                                  );
                                                e.stopPropagation();
                                                listData.setValue(data);
                                              } else {
                                                e.stopPropagation();
                                                listData.setValue([
                                                  ...listData?.value,
                                                  itm.id,
                                                ]);
                                              }
                                            } else {
                                              setShowDropdown(false);
                                              setShowInnerSearchBar(false);

                                              if (setTopNavSearch) {
                                                setTopNavSearch(false);
                                              }
                                              setShowSearch(false);
                                              listData.setValue(itm.id);
                                              if (listData?.setDisplayValue) {
                                                listData?.setDisplayValue(
                                                  itm?.display,
                                                );
                                              }
                                            }
                                          }}
                                          className={`w-full flex-wrap gap-y-gap-sm flex items-center gap-x-gap-md ${itm.id === listData?.value || String(listData?.value)?.includes(itm.id) ? "bg-primary-act" : ""}   relative global-hover-white z-z-index-content px-padding-sm ${inputData?.length - 1 === idx ? "" : "filter-search-dropdowndark-borderdiv"} `}
                                        >
                                          {Object.entries(itm)?.map(
                                            ([key, value]) => {
                                              if (
                                                key === "id" ||
                                                key === "display"
                                              ) {
                                                return true;
                                              } else {
                                                return (
                                                  <div className="flex-1 flex flex-col gap-y-gap-xs truncate break-all">
                                                    <div
                                                      className={`text-text-primary  max-w-full text-font-xs font-normal break-all `}
                                                    >
                                                      <span
                                                        className={`truncate break-all flex-1 ${String(value)?.toLowerCase()?.includes(searchText) ? "filtered-search-highlight" : ""}`}
                                                      >
                                                        {key ===
                                                        "Account_number" ? (
                                                          standardAccountNumberFormat(
                                                            value,
                                                            true,
                                                          )
                                                        ) : (
                                                          <TickerAnimation
                                                            text={value}
                                                          />
                                                        )}
                                                      </span>
                                                    </div>
                                                    <p className="text-text-secondary text-font-xxs font-normal break-all">
                                                      {key?.replaceAll(
                                                        "_",
                                                        " ",
                                                      )}
                                                    </p>
                                                  </div>
                                                );
                                              }
                                            },
                                          )}
                                          {listData?.isMulti ? (
                                            <img
                                              className="w-icon-sm h-icon-sm flex-shrink-0"
                                              src={
                                                listData?.value?.includes(
                                                  itm.id,
                                                )
                                                  ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                                                  : ICONS?.SELECT_ALL_WITH_WHITE_OUTLINE_CIRCLE
                                              }
                                              alt=""
                                            />
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                    {inputData?.length ===
                                      GlobalConfiguration.searchMaxOptionLength &&
                                    !listData.isMulti ? (
                                      <p className="text-text-secondary filter-search-topdark-borderdiv relative px-padding-sm w-full  text-font-xs font-normal break-words">
                                        Displaying the top 5 results. Try
                                        refining your search for more specific
                                        options.
                                      </p>
                                    ) : null}
                                  </>
                                ) : (
                                  <p className="text-text-primary text-font-xs w-full text-center pt-padding-xs">
                                    No data found
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  listData.isDisabled ||
                                  (listData?.isAlphabetical &&
                                    Validation.alphanumericRegex.test(
                                      searchText?.replaceAll(" ", ""),
                                    ) &&
                                    !Validation.alphaRegex.test(
                                      searchText?.replaceAll(" ", ""),
                                    )) ||
                                  ((listData?.name
                                    ?.toLowerCase()
                                    ?.includes("amount") ||
                                    listData?.valueType === "number") &&
                                    (isNaN(searchText) ||
                                      searchText?.length ===
                                        GlobalConfiguration?.lengthCheckZero)) ||
                                  (!(
                                    listData?.name
                                      ?.toLowerCase()
                                      ?.includes("amount") ||
                                    listData?.valueType === "number"
                                  ) &&
                                    searchText?.length <
                                      GlobalConfiguration.searchMinLength)
                                )
                                  return;

                                if (listData.isDropdown) {
                                  setActive("");
                                  setdropdownanimate(true);
                                  setActive(listData.name);
                                  setCurrentDropdown(index);
                                  setShowDropdown(true);
                                } else {
                                  if (listData?.setDisplayValue) {
                                    listData?.setDisplayValue(searchText);
                                  }
                                  listData.setValue(searchText);
                                  setShowInnerSearchBar(false);

                                  if (setTopNavSearch) {
                                    setTopNavSearch(false);
                                  }
                                  setShowDropdown(false);
                                  setShowSearch(false);
                                }
                              }}
                              className={`flex h-auto break-words gap-x-gap-sm ${
                                listData.isDisabled ||
                                (listData?.isAlphabetical &&
                                  Validation.alphanumericRegex.test(
                                    searchText?.replaceAll(" ", ""),
                                  ) &&
                                  !Validation.alphaRegex.test(
                                    searchText?.replaceAll(" ", ""),
                                  )) ||
                                ((listData?.name
                                  ?.toLowerCase()
                                  ?.includes("amount") ||
                                  listData?.valueType === "number") &&
                                  (isNaN(searchText) ||
                                    searchText?.length ===
                                      GlobalConfiguration?.lengthCheckZero)) ||
                                (!(
                                  listData?.name
                                    ?.toLowerCase()
                                    ?.includes("amount") ||
                                  listData?.valueType === "number"
                                ) &&
                                  searchText?.length <
                                    GlobalConfiguration.searchMinLength)
                                  ? "cursor-not-allowed opacity-light-opacity"
                                  : "cursor-pointer"
                              }  filter-search-borderdiv w-full justify-between items-center rounded-border-radius-md px-padding-sm py-padding-xs relative ${
                                listData.isDisabled ||
                                (listData?.isAlphabetical &&
                                  Validation.alphanumericRegex.test(
                                    searchText?.replaceAll(" ", ""),
                                  ) &&
                                  !Validation.alphaRegex.test(
                                    searchText?.replaceAll(" ", ""),
                                  )) ||
                                ((listData?.name
                                  ?.toLowerCase()
                                  ?.includes("amount") ||
                                  listData?.valueType === "number") &&
                                  (isNaN(searchText) ||
                                    searchText?.length ===
                                      GlobalConfiguration?.lengthCheckZero)) ||
                                (!(
                                  listData?.name
                                    ?.toLowerCase()
                                    ?.includes("amount") ||
                                  listData?.valueType === "number"
                                ) &&
                                  searchText?.length <
                                    GlobalConfiguration.searchMinLength)
                                  ? ""
                                  : "global-hover-white"
                              } z-z-index-content`}
                            >
                              <LineMapper
                                data={listData}
                                key={index}
                                index={index}
                              />
                            </div>
                          )}
                        </>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        </div>
      </div>
    </div>
  );
};

export default FilteredSearchField;
